import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, tap } from 'rxjs/operators';
import { EnvService } from '../../services/env.service';
import { UserService } from '../../core/services/user.service';
import { IUser } from '../../core/interfaces/user.interface';
import { TutorialStatus } from './tutorial.types';

type ProgressMap = Record<string, TutorialStatus>;

/**
 * Estado del asistente por USUARIO, guardado en la cuenta (`User.tutorialLocal`).
 *  - `paused`: tutoriales que el usuario dejó a medias (Escape / "Salir"). Guarda
 *    el paso para poder RETOMAR. En el mapa de progreso sigue `notDone`.
 *  - `globalDisabled`: apaga TODO el asistente (invitaciones + botón de retomar).
 *  - `lastOfferTs`: última vez (epoch ms) que se OFRECIÓ cada tutorial con la
 *    píldora de invitación. Es lo que hace que la × ("más tarde") valga entre
 *    sesiones.
 */
interface GuidedTourLocalState {
  paused: Record<string, { step: number; ts: number }>;
  globalDisabled: boolean;
  lastOfferTs: Record<string, number>;
}

/** Enfriamiento entre invitaciones al mismo tutorial: 7 días. */
const OFFER_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Coalesce de escrituras: varias mutaciones seguidas ("reiniciar" = despausar +
 * olvidar enfriamiento) viajan en un solo PUT.
 */
const PUSH_DEBOUNCE_MS = 400;

function emptyLocal(): GuidedTourLocalState {
  return { paused: {}, globalDisabled: false, lastOfferTs: {} };
}

/**
 * Persistencia del asistente de tutoriales. NADA vive en el navegador: la fuente
 * de verdad es la cuenta del usuario (paths relativos a `env.URL_API_V1`; el
 * interceptor agrega el segmento de fecha que el back ignora):
 *  - `GET  /user/me/tutorials`           → progreso + estado del asistente,
 *  - `PUT  /user/me/tutorials/:id`       → estado de un tutorial,
 *  - `PUT  /user/me/tutorials-local`     → estado del asistente (foto completa),
 *  - `GET  /user/config/tutorials`       → interruptor de organización,
 *  - `PUT  /user/admin/tutorials-enabled`→ cambiarlo (solo rol con editRoles).
 */
@Injectable({ providedIn: 'root' })
export class TutorialProgressService {
  private readonly baseUrl: string;
  /** Progreso. Arranca vacío y lo hidrata `refreshFromServer()`. */
  private readonly progress$ = new BehaviorSubject<ProgressMap>({});
  /** Estado del asistente (paso pausado, interruptor, enfriamiento). */
  private readonly local$ = new BehaviorSubject<GuidedTourLocalState>(
    emptyLocal(),
  );
  /**
   * Interruptor MAESTRO de tutoriales para toda la organización. Default
   * `true`; lo corrige `fetchAdminEnabled()`.
   */
  private readonly adminEnabled$ = new BehaviorSubject<boolean>(
    this.readAdminEnabled(),
  );

  /** Disparador de la escritura del estado del asistente (debounced). */
  private readonly push$ = new Subject<void>();
  /** ¿Ya llegó el estado del servidor? Antes de eso no se pisa nada allá. */
  private hydrated = false;
  /**
   * Cambios hechos ANTES de la hidratación (un "más tarde" muy temprano). Al
   * hidratar ganan sobre lo que traiga el servidor y se envían.
   */
  private pending: Partial<GuidedTourLocalState> = {};

  constructor(
    private http: HttpClient,
    private env: EnvService,
    private userService: UserService,
  ) {
    this.baseUrl = `${env.URL_API_V1}/user`;
    this.push$
      .pipe(
        debounceTime(PUSH_DEBOUNCE_MS),
        // `switchMap` cancela el PUT anterior: dos escrituras seguidas no
        // pueden llegar al revés y dejar guardado el estado viejo.
        switchMap(() => this.putLocal()),
      )
      .subscribe();
  }

  /** Snapshot sincrónico del mapa. */
  getMap(): ProgressMap {
    return this.progress$.value;
  }

  /** Stream reactivo del mapa (para la sección Tutoriales del Perfil). */
  observe(): Observable<ProgressMap> {
    return this.progress$.asObservable();
  }

  /** Estado de un tutorial; ausente ⇒ 'notDone'. */
  getStatus(id: string): TutorialStatus {
    return this.progress$.value[id] ?? 'notDone';
  }

  /**
   * Fija el estado (idempotente). Actualiza local de inmediato (optimista) y
   * persiste en backend; si falla la red, conserva el valor local.
   */
  setStatus(id: string, status: TutorialStatus): Observable<void> {
    this.applyLocal(id, status);
    return this.http
      .put<{ ok: boolean; tutorialProgress?: ProgressMap }>(
        `${this.baseUrl}/me/tutorials/${id}`,
        { status },
      )
      .pipe(
        tap((res) => {
          if (res?.tutorialProgress) this.replaceAll(res.tutorialProgress);
        }),
        map(() => void 0),
        catchError(() => of(void 0)),
      );
  }

  /**
   * Trae del backend el progreso Y el estado del asistente en el mismo GET. Es
   * la ÚNICA hidratación.
   */
  refreshFromServer(): Observable<ProgressMap> {
    return this.http
      .get<{
        ok: boolean;
        tutorialProgress?: ProgressMap;
        tutorialLocal?: Partial<GuidedTourLocalState>;
      }>(`${this.baseUrl}/me/tutorials`)
      .pipe(
        tap((res) => this.hydrateLocal(res?.tutorialLocal)),
        map((res) => res?.tutorialProgress || {}),
        tap((mapa) => this.replaceAll(mapa)),
        catchError(() => of(this.progress$.value)),
      );
  }

  /**
   * ¿Llegó bien el GET de hidratación en esta sesión? Un GET fallido deja el
   * mapa VACÍO —indistinguible de "no vio nada"—, así que quien OFRECE un
   * recorrido tiene que consultarlo.
   */
  isHydrated(): boolean {
    return this.hydrated;
  }

  /**
   * Vacía TODO el estado en memoria (logout). `doLogout` de Analyst no recarga
   * la página: sin esto, el siguiente usuario de la misma pestaña veía el
   * progreso del anterior hasta que llegara su GET, y un PUT tutorials-local
   * pendiente podía subir el estado del primero a la cuenta del segundo.
   */
  reset(): void {
    this.hydrated = false;
    this.pending = {};
    this.progress$.next({});
    this.local$.next(emptyLocal());
    this.adminEnabled$.next(true);
  }

  // ── Estado del asistente (paso pausado + interruptor + enfriamiento) ───────

  /** Stream del estado del asistente (para que el botón de retomar reaccione). */
  observeLocal(): Observable<GuidedTourLocalState> {
    return this.local$.asObservable();
  }

  /** ¿El asistente de tutoriales está apagado por completo? */
  isGloballyDisabled(): boolean {
    return this.local$.value.globalDisabled === true;
  }

  setGloballyDisabled(off: boolean): void {
    this.patchLocal({ globalDisabled: off });
  }

  /** Paso en el que quedó pausado un tutorial, o null si no está pausado. */
  getPausedStep(id: string): number | null {
    const p = this.local$.value.paused[id];
    return p ? p.step : null;
  }

  isPaused(id: string): boolean {
    return !!this.local$.value.paused[id];
  }

  /** Marca un tutorial como pausado en el paso `step` (para retomarlo luego). */
  setPausedStep(id: string, step: number): void {
    const paused = {
      ...this.local$.value.paused,
      [id]: { step, ts: Date.now() },
    };
    this.patchLocal({ paused });
  }

  /** Quita el estado de pausa (al completar, reiniciar o desactivar). */
  clearPaused(id: string): void {
    if (!this.local$.value.paused[id]) return;
    const paused = { ...this.local$.value.paused };
    delete paused[id];
    this.patchLocal({ paused });
  }

  /** Ids pausados, del más reciente al más antiguo (para elegir cuál retomar). */
  pausedIdsByRecency(): string[] {
    const paused = this.local$.value.paused;
    return Object.keys(paused).sort((a, b) => paused[b].ts - paused[a].ts);
  }

  // ── Enfriamiento de la invitación ───────────────────────────────────────────

  /** ¿Ya se ofreció este tutorial hace poco? Mientras sea true no se re-ofrece. */
  wasOfferedRecently(id: string): boolean {
    const ts = this.local$.value.lastOfferTs[id];
    if (!ts) return false;
    return Date.now() - ts < OFFER_COOLDOWN_MS;
  }

  /** Registra que la invitación se mostró (arranca el enfriamiento). */
  markOffered(id: string): void {
    const lastOfferTs = { ...this.local$.value.lastOfferTs, [id]: Date.now() };
    this.patchLocal({ lastOfferTs });
  }

  /** Olvida el enfriamiento (al reactivar un tutorial desde el perfil). */
  clearOffered(id: string): void {
    if (!this.local$.value.lastOfferTs[id]) return;
    const lastOfferTs = { ...this.local$.value.lastOfferTs };
    delete lastOfferTs[id];
    this.patchLocal({ lastOfferTs });
  }

  // ── Interruptor MAESTRO de tutoriales (toda la organización) ────────────────

  /** ¿Los tutoriales están ACTIVADOS para toda la organización? */
  isAdminEnabled(): boolean {
    return this.adminEnabled$.value;
  }

  observeAdminEnabled(): Observable<boolean> {
    return this.adminEnabled$.asObservable();
  }

  /** Re-sincroniza el flag maestro desde el backend (GET, cualquier usuario). */
  fetchAdminEnabled(): Observable<boolean> {
    return this.http
      .get<{ ok: boolean; tutorialsEnabled?: boolean }>(
        `${this.baseUrl}/config/tutorials`,
      )
      .pipe(
        map((r) => r?.tutorialsEnabled !== false),
        tap((v) => this.applyAdminEnabled(v)),
        catchError(() => of(this.adminEnabled$.value)),
      );
  }

  /**
   * Enciende/apaga los tutoriales para TODA la organización. Solo un rol con
   * `editRoles` (el back responde 403 al resto; ver `canManageOrgTutorials`).
   */
  setAdminEnabled(enabled: boolean): Observable<boolean> {
    this.applyAdminEnabled(enabled); // optimista
    return this.http
      .put<{ ok: boolean; tutorialsEnabled?: boolean }>(
        `${this.baseUrl}/admin/tutorials-enabled`,
        { enabled },
      )
      .pipe(
        map((r) => r?.tutorialsEnabled !== false),
        tap((v) => this.applyAdminEnabled(v)),
        catchError(() => of(this.adminEnabled$.value)),
      );
  }

  private applyAdminEnabled(enabled: boolean): void {
    this.adminEnabled$.next(enabled);
    const user: any = this.userService.currentUser;
    if (user) user.tutorialsEnabled = enabled;
  }

  /** Usuario de la sesión viva (para evaluar la `audiencia` de cada tutorial). */
  currentUser(): IUser | null {
    return this.userService.currentUser || null;
  }

  /**
   * ¿La sesión puede encender/apagar los tutoriales de toda la organización?
   * En OSINT era `isSuperAdmin()`; Analyst no tiene superAdmin, así que lo
   * gobierna el flag `editRoles` del rol (mismo chequeo que hace el back).
   */
  canManageOrgTutorials(): boolean {
    return !!this.userService.currentUser?.role?.editRoles;
  }

  /** ¿El rol de la sesión tiene este flag? (pasos con `requiresPermission`). */
  hasPermission(permiso: string): boolean {
    const role: any = this.userService.currentUser?.role;
    return !!(role && role[permiso]);
  }

  private readAdminEnabled(): boolean {
    return (this.userService.currentUser as any)?.tutorialsEnabled !== false;
  }

  // ── internos ──────────────────────────────────────────────────────────────

  private applyLocal(id: string, status: TutorialStatus): void {
    this.progress$.next({ ...this.progress$.value, [id]: status });
  }

  private replaceAll(mapa: ProgressMap): void {
    this.progress$.next(TutorialProgressService.normalizeMap(mapa));
  }

  /**
   * Normaliza el mapa de progreso a claves PLANAS con puntos. El back guarda
   * las claves con `~` en vez de `.` (Mongo anida las claves con puntos), y por
   * si acaso también aplana un mapa anidado.
   */
  private static normalizeMap(raw: any, prefix = ''): ProgressMap {
    const out: ProgressMap = {};
    if (!raw || typeof raw !== 'object') return out;
    for (const [k, v] of Object.entries(raw)) {
      const decoded = String(k).split('~').join('.');
      const key = prefix ? `${prefix}.${decoded}` : decoded;
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        Object.assign(out, TutorialProgressService.normalizeMap(v, key));
      } else if (typeof v === 'string') {
        out[key] = v as TutorialStatus;
      }
    }
    return out;
  }

  /** Sanea lo que llega del backend. */
  private static coerceLocal(raw: any): GuidedTourLocalState {
    const out = emptyLocal();
    if (!raw || typeof raw !== 'object') return out;
    if (raw.paused && typeof raw.paused === 'object') {
      for (const [k, v] of Object.entries<any>(raw.paused)) {
        if (!v || typeof v !== 'object') continue;
        if (!Number.isFinite(Number(v.step))) continue;
        out.paused[k] = {
          step: Number(v.step),
          ts: Number.isFinite(Number(v.ts)) ? Number(v.ts) : 0,
        };
      }
    }
    if (raw.lastOfferTs && typeof raw.lastOfferTs === 'object') {
      for (const [k, v] of Object.entries<any>(raw.lastOfferTs)) {
        if (Number.isFinite(Number(v))) out.lastOfferTs[k] = Number(v);
      }
    }
    out.globalDisabled = raw.globalDisabled === true;
    return out;
  }

  /**
   * Aplica el estado que trae el servidor. Lo que el usuario hizo ANTES de que
   * llegara gana y se re-envía: si el servidor pisara sin más, el "más tarde"
   * recién pulsado se perdería.
   */
  private hydrateLocal(raw: any): void {
    const server = TutorialProgressService.coerceLocal(raw);
    const p = this.pending;
    const hayPendiente =
      !!p.paused || !!p.lastOfferTs || p.globalDisabled !== undefined;
    const merged: GuidedTourLocalState = {
      paused: { ...server.paused, ...(p.paused || {}) },
      lastOfferTs: { ...server.lastOfferTs, ...(p.lastOfferTs || {}) },
      globalDisabled:
        p.globalDisabled !== undefined
          ? p.globalDisabled
          : server.globalDisabled,
    };
    this.pending = {};
    this.hydrated = true;
    this.local$.next(merged);
    if (hayPendiente) this.push$.next();
  }

  private patchLocal(patch: Partial<GuidedTourLocalState>): void {
    const next = { ...this.local$.value, ...patch };
    this.local$.next(next);
    if (!this.hydrated) {
      // Sin la foto del servidor todavía: se anota para no perderlo al hidratar
      // y NO se escribe (mandarlo ahora borraría lo que hay en la cuenta).
      this.pending = { ...this.pending, ...patch };
      return;
    }
    this.push$.next();
  }

  /** Escribe la foto COMPLETA del estado del asistente. El backend reemplaza. */
  private putLocal(): Observable<unknown> {
    const hasToken = !!(
      localStorage.getItem('token') || sessionStorage.getItem('token')
    );
    // Sin hidratar (p. ej. un reset por logout dentro del debounce) no se
    // escribe: la foto sería vacía o de otro usuario.
    if (!hasToken || !this.hydrated) return of(null);
    return this.http
      .put<{ ok: boolean }>(`${this.baseUrl}/me/tutorials-local`, this.local$.value)
      .pipe(catchError(() => of(null)));
  }
}
